import * as _ from 'lodash-es';
import moment from 'moment';
import type { Tagged } from 'type-fest';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import getFeature from '../../utils/features';

const HISTORY_FRAGMENT = gql(`
  fragment partitionHistories_history on History {
    id
    insertedAt
    authors {
      id
    }
    stats {
      insert
      delete
      comment
      resolve
    }
  }
`);

type History = DocumentType<typeof HISTORY_FRAGMENT>;

export const getHistoryRestrictionDate = (
  versionHistoryRestrictionDays = getFeature('version_history'),
) => moment().subtract(versionHistoryRestrictionDays + 1, 'days');

export const isHistoryAccessible = (
  insertedAt: string | number,
  versionHistoryRestrictionDays = getFeature('version_history'),
) => {
  if (versionHistoryRestrictionDays === Infinity) {
    return true;
  }

  const restrictedAt = getHistoryRestrictionDate(versionHistoryRestrictionDays);

  return new Date(insertedAt) > restrictedAt.toDate();
};

export type PartitionedHistory<T extends History> = Tagged<
  T & {
    isAccessible: boolean;
    hasStats: boolean;
  },
  'partitioned'
>;

const partitionHistories = <T extends History>(
  histories: T[],
  versionHistoryRestrictionDays = getFeature('version_history'),
): {
  accessibleHistories: PartitionedHistory<T>[];
  restrictedHistories: PartitionedHistory<T>[];
} => {
  const sortedHistories = histories.toSorted(
    (a, b) =>
      new Date(b.insertedAt).valueOf() - new Date(a.insertedAt).valueOf(),
  );

  const historiesWithStatsOrAuthors = sortedHistories
    .map((history) => {
      const { stats } = history;
      const hasStats = Object.values({
        ...stats,
        comment: stats.comment,
        resolve: stats.resolve,
        insert: stats.insert,
        delete: stats.delete,
      }).some((stat) => typeof stat === 'number' && stat > 0);

      return {
        ...history,
        hasStats,
      };
    })
    .filter(({ authors, hasStats }) => authors.length > 0 || hasStats);

  const [accessibleHistories, restrictedHistories] = _.partition(
    historiesWithStatsOrAuthors,
    (history) =>
      isHistoryAccessible(history.insertedAt, versionHistoryRestrictionDays),
  );

  return {
    accessibleHistories: accessibleHistories.map(
      (h) =>
        ({
          ...h,
          isAccessible: true,
        }) as PartitionedHistory<T>,
    ),
    restrictedHistories: restrictedHistories.map(
      (h) =>
        ({
          ...h,
          isAccessible: false,
        }) as PartitionedHistory<T>,
    ),
  };
};

export default partitionHistories;
